import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_COUNTRIES,
  RECEIVING_COUNTRIES,
  RECEIVE_COUNTRY_METADATA,
  RECEIVE_COUNTRY_FAILURE,
  SAVING_COUNTRY,
  SAVING_COUNTRY_FAILURE,
  UPDATING_COUNTRY,
  UPDATING_COUNTRY_FAILURE,
  DELETE_COUNTRY,
  DELETING_COUNTRY,
  DELETING_COUNTRY_FAILURE,
  INCREMENT_COUNTRY_COUNT,
  CLEAR_COUNTRY_STATE,
  RESET_COUNTRY_META,
  CLEAR_STATE
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState,
  deleteData
} from '@/store/modules/helpers/mutationHelpers'

const countrySchema = new schema.Entity('countries')
const countryListSchema = new schema.Array(countrySchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false
}

const actions = {
  fetchCountriesWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_COUNTRIES, true)
    return http.get(buildUrlWithQuery('countries', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_COUNTRIES, false)
        this.commit(RESET_COUNTRY_META)
        if (r.hasOwnProperty('results')) {
          const { results, next, previous, count } = r
          const { entities, result } = normalize(results, countryListSchema)
          this.commit(RECEIVE_COUNTRIES, { data: entities.countries })
          this.commit(RECEIVE_COUNTRY_METADATA, { next, previous, count, result: result.length })
          return { query, count, entities }
        }
      })
      .catch(e => {
        this.commit(RECEIVING_COUNTRIES, false)
        this.commit(RECEIVE_COUNTRY_FAILURE, handleXHRError(e))
      })
  }
}

const getters = {
  getCountries: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getCountryMetadata: (state) => {
    const { shown, count } = state
    if (!shown) {
      return { count: 0, shown: 0 }
    }
    return { count, shown }
  },
  getCountryById: (state) => (id) => {
    if (state.data) return state.data[id]
  },
  getCountrySaveStatus: (state) => {
    return state.saving
  },
  getCountryUpdateStatus: (state) => {
    return state.updating
  }
}

const mutations = {
  [RECEIVE_COUNTRIES] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_COUNTRIES] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_COUNTRY_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_COUNTRY] (state, payload) {
    state.saving = payload
  },
  [SAVING_COUNTRY_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_COUNTRY] (state, payload) {
    state.updating = payload
  },
  [UPDATING_COUNTRY_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_COUNTRY] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_COUNTRY] (state, payload) {
    state.deleting = payload
  },
  [DELETING_COUNTRY_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [INCREMENT_COUNTRY_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_COUNTRY_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_COUNTRY_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_COUNTRY_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
